import { faqJsonLd, useSeo } from "../lib/seo";
import { WHEELS_SEO } from "../data/wheelsSeo";
import { WheelsSeoSection } from "../components/seo/WheelsSeoSection";
import { useState } from "react";
import { Pencil, Plus, RotateCw, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { useData } from "../context/DataContext";
import { useReveal } from "../hooks/useReveal";
import { spinList, WHEEL_TEMPLATES } from "../engine/wheels";
import { uid } from "../lib/storage";
import { PageShell } from "../components/PageShell";
import { PopButton } from "../components/PopButton";
import { GuestBanner } from "../components/DecisionList";
import { ResultReveal } from "../components/reveal/ResultReveal";
import { WheelEditor } from "../components/wheels/WheelEditor";
import type { WheelList } from "../types";

const blank = (name = "", options: string[] = []): WheelList => ({ id: uid(), name, options, updated_at: "" });
const TONES = ["bg-pop-orange text-white", "bg-pop-teal", "bg-pop-purple text-white", "bg-pop-yellow", "bg-pop-pink text-white", "bg-pop-green text-white"];

function WheelCard({ list, index, onSpin, onEdit, onDelete }: { list: WheelList; index: number; onSpin: () => void; onEdit: () => void; onDelete: () => void }) {
  const [confirm, setConfirm] = useState(false);
  return (
    <div className={`rounded-[22px] border-[3px] border-ink p-5 pop-shadow ${TONES[index % TONES.length]}`} data-testid={`wheel-card-${index}`}>
      <p className="font-display text-2xl font-bold uppercase leading-tight [overflow-wrap:anywhere]" data-testid={`wheel-card-name-${index}`}>{list.name}</p>
      <p className="mt-1 line-clamp-2 text-sm font-semibold opacity-90">
        {list.options.map((o) => ((list.weights?.[o] ?? 1) > 1 ? `${o} ×${list.weights?.[o]}` : o)).join(" · ")}
      </p>
      <div className="mt-4 flex gap-2">
        <PopButton tone="white" size="sm" className="flex-1" onClick={onSpin} data-testid={`wheel-spin-button-${index}`}><RotateCw className="h-4 w-4" strokeWidth={3} />Spin</PopButton>
        <PopButton tone="white" size="sm" onClick={onEdit} aria-label="Edit wheel" data-testid={`wheel-edit-button-${index}`}><Pencil className="h-4 w-4" /></PopButton>
        <PopButton tone={confirm ? "ink" : "white"} size="sm" onClick={() => (confirm ? onDelete() : setConfirm(true))} aria-label="Delete wheel" data-testid={`wheel-delete-button-${index}`}>
          <Trash2 className="h-4 w-4" />{confirm && "Sure?"}
        </PopButton>
      </div>
    </div>
  );
}

export default function WheelsPage() {
  useSeo({ title: WHEELS_SEO.title, description: WHEELS_SEO.description, keywords: WHEELS_SEO.keywords, path: "/wheels", jsonLd: faqJsonLd(WHEELS_SEO.faqs) });
  const { lists, saveList, deleteList, signedIn } = useData();
  const { spec, show, close } = useReveal();
  const [editing, setEditing] = useState<WheelList | null>(null);
  const [active, setActive] = useState<WheelList | null>(null);

  const spin = (list: WheelList, exclude?: string) => {
    const { result, labels, weights } = spinList(list.options, exclude, list.weights);
    setActive(list);
    show({ kind: "wheel", title: list.name, result, anim: "wheel", reel: labels, weights, category: "wheel" });
  };

  return (
    <PageShell title="My wheels" subtitle="Save your own lists. Spin them whenever." testId="wheels-page">
      {!signedIn && <GuestBanner />}
      {editing ? (
        <WheelEditor initial={editing} onCancel={() => setEditing(null)}
          onSave={(l) => { saveList(l); setEditing(null); toast.success("Wheel saved"); }} />
      ) : (
        <PopButton tone="orange" size="lg" className="w-full" onClick={() => setEditing(blank())} data-testid="wheel-new-button"><Plus className="h-5 w-5" strokeWidth={3} />New wheel</PopButton>
      )}
      {!editing && !lists.length && (
        <div className="mt-6" data-testid="wheel-templates">
          <p className="mb-2 text-xs font-extrabold uppercase tracking-widest text-zinc-500">Or start from a template</p>
          <div className="flex flex-wrap gap-2">
            {WHEEL_TEMPLATES.map((t, i) => (
              <button key={t.name} className="chip hover:bg-pop-yellow" onClick={() => setEditing(blank(t.name, t.options))} data-testid={`wheel-template-${i}`}>{t.name}</button>
            ))}
          </div>
        </div>
      )}
      <div className="mt-6 grid gap-4 sm:grid-cols-2" data-testid="wheel-list">
        {lists.map((l, i) => (
          <WheelCard key={l.id} list={l} index={i} onSpin={() => spin(l)} onEdit={() => setEditing(l)} onDelete={() => { deleteList(l.id); toast.success("Wheel deleted"); }} />
        ))}
      </div>
      <WheelsSeoSection />
      <ResultReveal spec={spec} onClose={close} onAgain={() => active && spin(active, spec?.result)} againLabel="Spin again" />
    </PageShell>
  );
}
